import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FieldDefinition {
  _id: string;
  key: string;
  label: string;
  fieldType: 'text' | 'number' | 'date' | 'select' | 'boolean';
  options: string[];
  required: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface AdditionalInfoValue {
  fieldId: string;
  key: string;
  value: string;
}

interface AdditionalInfoFieldsProps {
  entity: 'lead' | 'deal';
  value: AdditionalInfoValue[];
  onChange: (values: AdditionalInfoValue[]) => void;
}

interface AdditionalInfoDisplayProps {
  entity: 'lead' | 'deal';
  values: AdditionalInfoValue[];
}

// ─── Hook to fetch field definitions ─────────────────────────────────────────

function useAdditionalInfoFields(entity: string) {
  return useQuery({
    queryKey: ['additional-info', 'fields', entity],
    queryFn: async () => {
      const { data } = await api.get<{ items: FieldDefinition[] }>(
        `/api/additional-info/fields?entity=${entity}`,
      );
      return data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Editable form section ───────────────────────────────────────────────────

/**
 * Renders user-defined custom fields for a given entity (lead or deal).
 * Fetches active field definitions from the API, renders one input per field,
 * and produces the [{fieldId, key, value}] array for persistence.
 *
 * If no fields are defined for the entity, renders nothing.
 */
export function AdditionalInfoFields({ entity, value, onChange }: AdditionalInfoFieldsProps) {
  const { data: fields, isLoading } = useAdditionalInfoFields(entity);

  if (isLoading || !fields || fields.length === 0) return null;

  const getFieldValue = (key: string): string => {
    const entry = value.find((v) => v.key === key);
    return entry?.value ?? '';
  };

  const setFieldValue = (field: FieldDefinition, newValue: string) => {
    const existing = value.filter((v) => v.key !== field.key);
    // Only include the field if it has a value — skip empty optional fields
    if (newValue.trim() || field.required) {
      existing.push({ fieldId: field._id, key: field.key, value: newValue });
    }
    onChange(existing);
  };

  return (
    <div className="space-y-4">
      <h4 className="text-body-sm font-semibold text-on-surface-variant uppercase tracking-wider border-b border-outline pb-2">
        Additional Information
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map((field) => {
          const currentValue = getFieldValue(field.key);

          switch (field.fieldType) {
            case 'text':
              return (
                <Input
                  key={field._id}
                  label={field.label + (field.required ? ' *' : '')}
                  value={currentValue}
                  onChange={(e) => setFieldValue(field, e.target.value)}
                  required={field.required}
                  placeholder={field.label}
                />
              );

            case 'number':
              return (
                <Input
                  key={field._id}
                  label={field.label + (field.required ? ' *' : '')}
                  type="number"
                  value={currentValue}
                  onChange={(e) => setFieldValue(field, e.target.value)}
                  required={field.required}
                  placeholder={field.label}
                />
              );

            case 'date':
              return (
                <Input
                  key={field._id}
                  label={field.label + (field.required ? ' *' : '')}
                  type="date"
                  value={currentValue}
                  onChange={(e) => setFieldValue(field, e.target.value)}
                  required={field.required}
                />
              );

            case 'select':
              return (
                <Select
                  key={field._id}
                  label={field.label + (field.required ? ' *' : '')}
                  value={currentValue}
                  onChange={(e) => setFieldValue(field, e.target.value)}
                  options={[
                    { value: '', label: `Select ${field.label}…` },
                    ...field.options.map((opt) => ({ value: opt, label: opt })),
                  ]}
                />
              );

            case 'boolean':
              return (
                <div key={field._id} className="flex items-center gap-2 py-2">
                  <input
                    type="checkbox"
                    id={`addinfo-${field.key}`}
                    checked={currentValue === 'true'}
                    onChange={(e) => setFieldValue(field, e.target.checked ? 'true' : 'false')}
                    className="h-4 w-4 rounded border-outline text-primary focus:ring-primary focus:ring-offset-0"
                  />
                  <label
                    htmlFor={`addinfo-${field.key}`}
                    className="text-data-md font-medium text-on-surface cursor-pointer"
                  >
                    {field.label}
                    {field.required && <span className="text-danger ml-0.5">*</span>}
                  </label>
                </div>
              );

            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}

// ─── Read-only display ───────────────────────────────────────────────────────

/**
 * Displays stored additional info values in a label/value read-only format.
 * Fetches ALL definitions (including inactive) to resolve labels for historical data.
 * Skips fields whose value is empty.
 *
 * If no values exist, renders nothing.
 */
export function AdditionalInfoDisplay({ entity, values }: AdditionalInfoDisplayProps) {
  const { data: fields } = useQuery({
    queryKey: ['additional-info', 'fields', entity, 'all'],
    queryFn: async () => {
      const { data } = await api.get<{ items: FieldDefinition[] }>(
        `/api/additional-info/fields?entity=${entity}&all=1`,
      );
      return data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Filter out empty values
  const filledValues = (values ?? []).filter((v) => v.value && v.value.trim());

  if (!filledValues.length) return null;

  const labelMap = new Map((fields ?? []).map((f) => [f.key, f]));

  const formatValue = (key: string, value: string): string => {
    const field = labelMap.get(key);
    if (!field) return value;
    switch (field.fieldType) {
      case 'boolean':
        return value === 'true' ? 'Yes' : 'No';
      case 'date':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return value;
        }
      default:
        return value;
    }
  };

  return (
    <div className="space-y-2">
      <h4 className="text-body-sm font-semibold text-on-surface-variant uppercase tracking-wider">
        Additional Information
      </h4>
      <div className="space-y-1.5">
        {filledValues.map((v) => {
          const field = labelMap.get(v.key);
          return (
            <div
              key={v.key}
              className="flex justify-between gap-4 border-b border-outline py-1.5 last:border-0"
            >
              <span className="text-body-sm text-on-surface-variant">
                {field?.label ?? v.key}
              </span>
              <span className="text-right text-data-md text-on-surface font-medium">
                {formatValue(v.key, v.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
